import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FileText,
  Calendar,
  Users,
  TrendingUp,
  Download,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Clock,
  Activity,
  BarChart3,
  Target,
  RefreshCw,
} from 'lucide-react';
import apiService from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <div className="pt-6">{children}</div>}
    </div>
  );
}

const EvaluatorReportsPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const [tabValue, setTabValue] = useState(0);
  const [evaluators, setEvaluators] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Daily Report State
  const [selectedEvaluatorDaily, setSelectedEvaluatorDaily] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [dailyReport, setDailyReport] = useState<any>(null);

  // Monthly Report State
  const [selectedEvaluatorMonthly, setSelectedEvaluatorMonthly] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [monthlyReport, setMonthlyReport] = useState<any>(null);

  // All Evaluators State
  const [allEvaluatorsYear, setAllEvaluatorsYear] = useState(new Date().getFullYear());
  const [allEvaluatorsMonth, setAllEvaluatorsMonth] = useState(new Date().getMonth() + 1);
  const [allEvaluatorsReport, setAllEvaluatorsReport] = useState<any[]>([]);

  useEffect(() => {
    fetchEvaluators();
  }, []);

  const fetchEvaluators = async () => {
    try {
      const data = await apiService.getEvaluators();

      if (!data || data.length === 0) {
        setEvaluators([]);
        setError(t('evaluatorReports.errors.noEvaluators'));
      } else {
        setEvaluators(data);
        setError('');
      }
    } catch (err: any) {
      setEvaluators([]);

      if (err.response?.status === 401) {
        setError(t('evaluatorReports.errors.authError'));
      } else if (err.response?.status === 403) {
        setError(t('evaluatorReports.errors.permissionError'));
      } else {
        const errorMessage = err.response?.data?.message || t('evaluatorReports.errors.loadFailed');
        setError(errorMessage);
      }
    }
  };

  const handleTabChange = (newValue: number) => {
    setTabValue(newValue);
    setError('');
  };

  // Daily Report Functions
  const fetchDailyReport = async () => {
    if (!selectedEvaluatorDaily || !startDate || !endDate) {
      setError(t('evaluatorReports.errors.selectEvaluatorAndDates'));
      return;
    }

    try {
      setLoading(true);
      setError('');
      const data = await apiService.getEvaluatorDailyReport(
        selectedEvaluatorDaily,
        startDate,
        endDate
      );
      setDailyReport(data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('evaluatorReports.errors.dailyReportFailed'));
    } finally {
      setLoading(false);
    }
  };

  const downloadDailyReportPDF = async () => {
    if (!selectedEvaluatorDaily || !startDate || !endDate) {
      setError(t('evaluatorReports.errors.selectEvaluatorAndDates'));
      return;
    }

    try {
      setLoading(true);
      const blob = await apiService.downloadEvaluatorDailyReportPDF(
        selectedEvaluatorDaily,
        startDate,
        endDate
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `evaluator_daily_report_${selectedEvaluatorDaily}_${startDate}_${endDate}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.message || t('evaluatorReports.errors.pdfDownloadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Monthly Report Functions
  const fetchMonthlyReport = async () => {
    if (!selectedEvaluatorMonthly) {
      setError(t('evaluatorReports.errors.selectEvaluator'));
      return;
    }

    try {
      setLoading(true);
      setError('');
      const data = await apiService.getEvaluatorMonthlyReport(
        selectedEvaluatorMonthly,
        selectedYear,
        selectedMonth
      );
      setMonthlyReport(data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('evaluatorReports.errors.monthlyReportFailed'));
    } finally {
      setLoading(false);
    }
  };

  const downloadMonthlyReportPDF = async () => {
    if (!selectedEvaluatorMonthly) {
      setError(t('evaluatorReports.errors.selectEvaluator'));
      return;
    }

    try {
      setLoading(true);
      const blob = await apiService.downloadEvaluatorMonthlyReportPDF(
        selectedEvaluatorMonthly,
        selectedYear,
        selectedMonth
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `evaluator_monthly_report_${selectedEvaluatorMonthly}_${selectedYear}_${selectedMonth}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.message || t('evaluatorReports.errors.pdfDownloadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // All Evaluators Functions
  const fetchAllEvaluatorsReport = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiService.getAllEvaluatorsMonthly(allEvaluatorsYear, allEvaluatorsMonth);
      setAllEvaluatorsReport(data);
    } catch (err: any) {
      setError(err.response?.data?.message || t('evaluatorReports.errors.reportFailed'));
    } finally {
      setLoading(false);
    }
  };

  const downloadAllEvaluatorsReportPDF = async () => {
    try {
      setLoading(true);
      const blob = await apiService.downloadAllEvaluatorsMonthlyPDF(allEvaluatorsYear, allEvaluatorsMonth);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `all_evaluators_monthly_report_${allEvaluatorsYear}_${allEvaluatorsMonth}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.message || t('evaluatorReports.errors.pdfDownloadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const years = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);
  const months = [
    { value: 1, label: t('months.january') },
    { value: 2, label: t('months.february') },
    { value: 3, label: t('months.march') },
    { value: 4, label: t('months.april') },
    { value: 5, label: t('months.may') },
    { value: 6, label: t('months.june') },
    { value: 7, label: t('months.july') },
    { value: 8, label: t('months.august') },
    { value: 9, label: t('months.september') },
    { value: 10, label: t('months.october') },
    { value: 11, label: t('months.november') },
    { value: 12, label: t('months.december') },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-brand-50/30 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Premium Header */}
        <div className="relative overflow-hidden rounded-3xl shadow-2xl">
          <div className="absolute inset-0 bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700 opacity-95"></div>
          <div className="absolute inset-0 backdrop-blur-3xl bg-white/10"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -mr-48 -mt-48"></div>
          <div className="absolute bottom-0 left-0 w-72 h-72 bg-brand-800/20 rounded-full -ml-36 -mb-36"></div>

          <div className="relative px-8 py-10">
            <div className="flex items-center gap-4 mb-2">
              <div className="p-3 bg-white/20 backdrop-blur-sm rounded-2xl">
                <BarChart3 className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-4xl font-black text-white" dir={isRTL ? 'rtl' : 'ltr'}>
                {t('evaluatorReports.title')}
              </h1>
            </div>
            <p className="text-brand-50 text-lg font-medium" dir={isRTL ? 'rtl' : 'ltr'}>
              {t('evaluatorReports.subtitle')}
            </p>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 rounded-2xl p-4 flex items-start gap-3 animate-fadeIn">
            <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-red-800 font-medium" dir="rtl">{error}</p>
            </div>
            <button
              onClick={() => setError('')}
              className="text-red-500 hover:text-red-700 transition-colors"
            >
              ×
            </button>
          </div>
        )}

        {/* Tabs Container */}
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Tab Headers */}
          <div className="flex border-b border-gray-200 bg-gray-50">
            <button
              onClick={() => handleTabChange(0)}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-bold transition-all duration-300 ${
                tabValue === 0
                  ? 'bg-white text-brand-600 border-b-4 border-brand-600'
                  : 'text-gray-600 hover:text-brand-600 hover:bg-gray-100'
              }`}
            >
              <Calendar className="w-5 h-5" />
              <span dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.tabs.dailyReport')}</span>
            </button>
            <button
              onClick={() => handleTabChange(1)}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-bold transition-all duration-300 ${
                tabValue === 1
                  ? 'bg-white text-brand-600 border-b-4 border-brand-600'
                  : 'text-gray-600 hover:text-brand-600 hover:bg-gray-100'
              }`}
            >
              <FileText className="w-5 h-5" />
              <span dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.tabs.monthlyReport')}</span>
            </button>
            <button
              onClick={() => handleTabChange(2)}
              className={`flex-1 flex items-center justify-center gap-2 px-6 py-4 font-bold transition-all duration-300 ${
                tabValue === 2
                  ? 'bg-white text-brand-600 border-b-4 border-brand-600'
                  : 'text-gray-600 hover:text-brand-600 hover:bg-gray-100'
              }`}
            >
              <TrendingUp className="w-5 h-5" />
              <span dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.tabs.allEvaluators')}</span>
            </button>
          </div>

          {/* Daily Report Tab */}
          <TabPanel value={tabValue} index={0}>
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
                {/* Evaluator Select */}
                <div className="lg:col-span-2">
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('evaluatorReports.form.evaluator')}
                  </label>
                  <select
                    value={selectedEvaluatorDaily}
                    onChange={(e) => setSelectedEvaluatorDaily(e.target.value)}
                    disabled={evaluators.length === 0}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-brand-500 focus:bg-white transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  >
                    <option value="">{t('evaluatorReports.form.selectEvaluator')}</option>
                    {evaluators.map((evaluator) => (
                      <option key={evaluator.id || evaluator._id} value={evaluator.id || evaluator._id}>
                        {evaluator.user?.firstName || evaluator.user?.first_name} {evaluator.user?.lastName || evaluator.user?.last_name}
                      </option>
                    ))}
                  </select>
                  {evaluators.length === 0 && (
                    <p className="text-xs text-red-500 mt-1" dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.errors.noEvaluatorsAvailable')}</p>
                  )}
                </div>

                {/* Start Date */}
                <div className="lg:col-span-1">
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('evaluatorReports.form.startDate')}
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-brand-500 focus:bg-white transition-all duration-300 font-medium"
                  />
                </div>

                {/* End Date */}
                <div className="lg:col-span-1">
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('evaluatorReports.form.endDate')}
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-brand-500 focus:bg-white transition-all duration-300 font-medium"
                  />
                </div>

                {/* View Button */}
                <div className="lg:col-span-1">
                  <label className="block text-sm font-bold text-gray-700 mb-2 opacity-0">Action</label>
                  <button
                    onClick={fetchDailyReport}
                    disabled={loading}
                    className="w-full px-6 py-3 bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 text-white font-bold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-5 h-5" />
                    )}
                    <span>{t('evaluatorReports.form.view')}</span>
                  </button>
                </div>

                {/* PDF Button */}
                <div className="lg:col-span-1">
                  <label className="block text-sm font-bold text-gray-700 mb-2 opacity-0">Action</label>
                  <button
                    onClick={downloadDailyReportPDF}
                    disabled={loading || !dailyReport || !dailyReport.days || dailyReport.days.length === 0}
                    className="w-full px-6 py-3 bg-white border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-bold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    <span>{t('evaluatorReports.form.downloadPDF')}</span>
                  </button>
                </div>
              </div>

              {/* Daily Report Table */}
              {dailyReport && dailyReport.days && dailyReport.days.length > 0 && (
                <div className="space-y-4 animate-fadeIn">
                  <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-brand-50 to-emerald-50 rounded-2xl border border-brand-100">
                    <Users className="w-6 h-6 text-brand-600" />
                    <h3 className="text-xl font-black text-gray-800" dir={isRTL ? 'rtl' : 'ltr'}>
                      {t('evaluatorReports.dailyReport.evaluatorLabel')}: {dailyReport.evaluatorName}
                    </h3>
                  </div>

                  <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-lg">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700">
                            <th className="px-6 py-4 text-right text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.dailyReport.tableHeaders.date')}</th>
                            <th className="px-6 py-4 text-right text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.dailyReport.tableHeaders.day')}</th>
                            <th className="px-6 py-4 text-center text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.dailyReport.tableHeaders.totalComplaints')}</th>
                            <th className="px-6 py-4 text-center text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.dailyReport.tableHeaders.pending')}</th>
                            <th className="px-6 py-4 text-center text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.dailyReport.tableHeaders.inProgress')}</th>
                            <th className="px-6 py-4 text-center text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.dailyReport.tableHeaders.resolved')}</th>
                            <th className="px-6 py-4 text-center text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.dailyReport.tableHeaders.closed')}</th>
                            <th className="px-6 py-4 text-center text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.dailyReport.tableHeaders.reworkRequested')}</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {dailyReport.days.map((day: any, index: number) => (
                            <tr key={index} className="hover:bg-brand-50/30 transition-colors">
                              <td className="px-6 py-4 text-right font-semibold text-gray-800" dir="rtl">{day.date}</td>
                              <td className="px-6 py-4 text-right font-medium text-gray-600" dir="rtl">{day.day_name}</td>
                              <td className="px-6 py-4 text-center font-bold text-brand-600">{day.total_complaints}</td>
                              <td className="px-6 py-4 text-center">
                                <span className="inline-flex items-center px-3 py-1 rounded-xl text-xs font-bold bg-orange-100 text-orange-800 border border-orange-200">
                                  {day.pending}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-center">
                                <span className="inline-flex items-center px-3 py-1 rounded-xl text-xs font-bold bg-blue-100 text-blue-800 border border-blue-200">
                                  {day.in_progress}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-center">
                                <span className="inline-flex items-center px-3 py-1 rounded-xl text-xs font-bold bg-green-100 text-green-800 border border-green-200">
                                  {day.resolved}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-center">
                                <span className="inline-flex items-center px-3 py-1 rounded-xl text-xs font-bold bg-gray-100 text-gray-800 border border-gray-200">
                                  {day.closed}
                                </span>
                              </td>
                              <td className="px-6 py-4 text-center">
                                <span className="inline-flex items-center px-3 py-1 rounded-xl text-xs font-bold bg-red-100 text-red-800 border border-red-200">
                                  {day.rework_requested}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabPanel>

          {/* Monthly Report Tab */}
          <TabPanel value={tabValue} index={1}>
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-6">
                {/* Evaluator Select */}
                <div className="lg:col-span-3">
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('evaluatorReports.form.evaluator')}
                  </label>
                  <select
                    value={selectedEvaluatorMonthly}
                    onChange={(e) => setSelectedEvaluatorMonthly(e.target.value)}
                    disabled={evaluators.length === 0}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-brand-500 focus:bg-white transition-all duration-300 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    dir={isRTL ? 'rtl' : 'ltr'}
                  >
                    <option value="">{t('evaluatorReports.form.selectEvaluator')}</option>
                    {evaluators.map((evaluator) => (
                      <option key={evaluator.id || evaluator._id} value={evaluator.id || evaluator._id}>
                        {evaluator.user?.firstName || evaluator.user?.first_name} {evaluator.user?.lastName || evaluator.user?.last_name}
                      </option>
                    ))}
                  </select>
                  {evaluators.length === 0 && (
                    <p className="text-xs text-red-500 mt-1" dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.errors.noEvaluatorsAvailable')}</p>
                  )}
                </div>

                {/* Year Select */}
                <div className="lg:col-span-1">
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('evaluatorReports.form.year')}
                  </label>
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-brand-500 focus:bg-white transition-all duration-300 font-medium"
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Month Select */}
                <div className="lg:col-span-1">
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('evaluatorReports.form.month')}
                  </label>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-brand-500 focus:bg-white transition-all duration-300 font-medium"
                    dir="rtl"
                  >
                    {months.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* View Button */}
                <div className="lg:col-span-1">
                  <label className="block text-sm font-bold text-gray-700 mb-2 opacity-0">Action</label>
                  <button
                    onClick={fetchMonthlyReport}
                    disabled={loading}
                    className="w-full px-6 py-3 bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 text-white font-bold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-5 h-5" />
                    )}
                    <span>{t('evaluatorReports.form.view')}</span>
                  </button>
                </div>

                {/* PDF Button - New Row */}
                <div className="lg:col-span-1">
                  <label className="block text-sm font-bold text-gray-700 mb-2 opacity-0">Action</label>
                  <button
                    onClick={downloadMonthlyReportPDF}
                    disabled={loading || !monthlyReport}
                    className="w-full px-6 py-3 bg-white border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-bold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    <span>{t('evaluatorReports.form.downloadPDF')}</span>
                  </button>
                </div>
              </div>

              {/* Monthly Report Display */}
              {monthlyReport && (
                <div className="space-y-6 animate-fadeIn">
                  {/* Evaluator Name */}
                  <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-brand-50 to-emerald-50 rounded-2xl border border-brand-100">
                    <Users className="w-6 h-6 text-brand-600" />
                    <h3 className="text-xl font-black text-gray-800" dir={isRTL ? 'rtl' : 'ltr'}>
                      {t('evaluatorReports.monthlyReport.evaluatorLabel')}: {monthlyReport.evaluatorName}
                    </h3>
                  </div>

                  {/* Stats Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-br from-brand-50 to-emerald-50 rounded-2xl shadow-lg border border-brand-100 p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-brand-600 rounded-xl">
                          <FileText className="w-5 h-5 text-white" />
                        </div>
                        <p className="text-sm font-bold text-gray-600" dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.monthlyReport.stats.totalComplaints')}</p>
                      </div>
                      <p className="text-3xl font-black text-brand-600">{monthlyReport.total_complaints || 0}</p>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl shadow-lg border border-green-100 p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-green-600 rounded-xl">
                          <CheckCircle className="w-5 h-5 text-white" />
                        </div>
                        <p className="text-sm font-bold text-gray-600" dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.monthlyReport.stats.completedComplaints')}</p>
                      </div>
                      <p className="text-3xl font-black text-green-600">{monthlyReport.completedComplaints || 0}</p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl shadow-lg border border-purple-100 p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-purple-600 rounded-xl">
                          <Calendar className="w-5 h-5 text-white" />
                        </div>
                        <p className="text-sm font-bold text-gray-600" dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.monthlyReport.stats.activeWorkDays')}</p>
                      </div>
                      <p className="text-3xl font-black text-purple-600">{monthlyReport.totalWorkDays || 0}</p>
                      <p className="text-xs text-purple-600 font-medium mt-1" dir={isRTL ? 'rtl' : 'ltr'}>
                        {t('evaluatorReports.monthlyReport.stats.outOf')} {monthlyReport.workDaysInMonth || 0} {t('evaluatorReports.monthlyReport.stats.workDays')}
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-2xl shadow-lg border border-orange-100 p-6">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 bg-orange-600 rounded-xl">
                          <Activity className="w-5 h-5 text-white" />
                        </div>
                        <p className="text-sm font-bold text-gray-600" dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.monthlyReport.stats.averagePerDay')}</p>
                      </div>
                      <p className="text-3xl font-black text-orange-600">{monthlyReport.averageComplaintsPerWorkDay || 0}</p>
                    </div>
                  </div>

                  {/* Status Breakdown */}
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                    <h3 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2" dir={isRTL ? 'rtl' : 'ltr'}>
                      <BarChart3 className="w-5 h-5 text-brand-600" />
                      {t('evaluatorReports.monthlyReport.statusBreakdown.title')}
                    </h3>
                    <div className="flex flex-wrap gap-3">
                      <span className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-bold bg-orange-100 text-orange-800 border border-orange-200" dir={isRTL ? 'rtl' : 'ltr'}>
                        {t('evaluatorReports.monthlyReport.statusBreakdown.pending')}: {monthlyReport.status_breakdown?.pending || 0}
                      </span>
                      <span className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-bold bg-blue-100 text-blue-800 border border-blue-200" dir={isRTL ? 'rtl' : 'ltr'}>
                        {t('evaluatorReports.monthlyReport.statusBreakdown.inProgress')}: {monthlyReport.status_breakdown?.in_progress || 0}
                      </span>
                      <span className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-bold bg-green-100 text-green-800 border border-green-200" dir={isRTL ? 'rtl' : 'ltr'}>
                        {t('evaluatorReports.monthlyReport.statusBreakdown.resolved')}: {monthlyReport.status_breakdown?.resolved || 0}
                      </span>
                      <span className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-bold bg-gray-100 text-gray-800 border border-gray-200" dir={isRTL ? 'rtl' : 'ltr'}>
                        {t('evaluatorReports.monthlyReport.statusBreakdown.closed')}: {monthlyReport.status_breakdown?.closed || 0}
                      </span>
                      <span className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-bold bg-red-100 text-red-800 border border-red-200" dir={isRTL ? 'rtl' : 'ltr'}>
                        {t('evaluatorReports.monthlyReport.statusBreakdown.reworkRequested')}: {monthlyReport.status_breakdown?.rework_requested || 0}
                      </span>
                    </div>
                  </div>

                  {/* SLA Compliance */}
                  <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6">
                    <h3 className="text-lg font-black text-gray-800 mb-4 flex items-center gap-2" dir={isRTL ? 'rtl' : 'ltr'}>
                      <Target className="w-5 h-5 text-brand-600" />
                      {t('evaluatorReports.monthlyReport.slaCompliance.title')}
                    </h3>
                    <div className="flex flex-wrap items-center gap-4">
                      <span className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-bold bg-green-100 text-green-800 border border-green-200" dir={isRTL ? 'rtl' : 'ltr'}>
                        {t('evaluatorReports.monthlyReport.slaCompliance.onTime')}: {monthlyReport.slaCompliance?.onTime || 0}
                      </span>
                      <span className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-bold bg-gray-100 text-gray-800 border border-gray-200" dir={isRTL ? 'rtl' : 'ltr'}>
                        {t('evaluatorReports.monthlyReport.slaCompliance.total')}: {monthlyReport.slaCompliance?.total || 0}
                      </span>
                      <span className="inline-flex items-center px-6 py-3 rounded-2xl text-lg font-black bg-gradient-to-r from-green-500 to-emerald-500 text-white shadow-lg" dir={isRTL ? 'rtl' : 'ltr'}>
                        {t('evaluatorReports.monthlyReport.slaCompliance.percentage')}: {monthlyReport.slaCompliance?.percentage || '0'}%
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </TabPanel>

          {/* All Evaluators Comparison Tab */}
          <TabPanel value={tabValue} index={2}>
            <div className="p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {/* Year Select */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('evaluatorReports.form.year')}
                  </label>
                  <select
                    value={allEvaluatorsYear}
                    onChange={(e) => setAllEvaluatorsYear(parseInt(e.target.value))}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-brand-500 focus:bg-white transition-all duration-300 font-medium"
                  >
                    {years.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Month Select */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2" dir={isRTL ? 'rtl' : 'ltr'}>
                    {t('evaluatorReports.form.month')}
                  </label>
                  <select
                    value={allEvaluatorsMonth}
                    onChange={(e) => setAllEvaluatorsMonth(parseInt(e.target.value))}
                    className="w-full px-4 py-3 bg-gray-50 border-2 border-gray-200 rounded-2xl focus:outline-none focus:border-brand-500 focus:bg-white transition-all duration-300 font-medium"
                    dir="rtl"
                  >
                    {months.map((month) => (
                      <option key={month.value} value={month.value}>
                        {month.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* View Button */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 opacity-0">Action</label>
                  <button
                    onClick={fetchAllEvaluatorsReport}
                    disabled={loading}
                    className="w-full px-6 py-3 bg-gradient-to-r from-brand-600 to-brand-700 hover:from-brand-700 hover:to-brand-800 text-white font-bold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <RefreshCw className="w-5 h-5" />
                    )}
                    <span dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.form.viewReport')}</span>
                  </button>
                </div>

                {/* PDF Button */}
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2 opacity-0">Action</label>
                  <button
                    onClick={downloadAllEvaluatorsReportPDF}
                    disabled={loading || allEvaluatorsReport.length === 0}
                    className="w-full px-6 py-3 bg-white border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white font-bold rounded-2xl transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <Download className="w-5 h-5" />
                    <span>{t('evaluatorReports.form.downloadPDF')}</span>
                  </button>
                </div>
              </div>

              {/* All Evaluators Report Table */}
              {allEvaluatorsReport.length > 0 && (
                <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-lg animate-fadeIn">
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-gradient-to-r from-brand-600 via-brand-700 to-emerald-700">
                          <th className="px-6 py-4 text-right text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.allEvaluatorsReport.tableHeaders.evaluator')}</th>
                          <th className="px-6 py-4 text-center text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.allEvaluatorsReport.tableHeaders.totalComplaints')}</th>
                          <th className="px-6 py-4 text-center text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.allEvaluatorsReport.tableHeaders.reworkCases')}</th>
                          <th className="px-6 py-4 text-center text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.allEvaluatorsReport.tableHeaders.reworkRate')}</th>
                          <th className="px-6 py-4 text-center text-sm font-black text-white uppercase tracking-wide" dir={isRTL ? 'rtl' : 'ltr'}>{t('evaluatorReports.allEvaluatorsReport.tableHeaders.avgReworkTime')}</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {allEvaluatorsReport.map((evaluator, index) => (
                          <tr key={index} className="hover:bg-brand-50/30 transition-colors">
                            <td className="px-6 py-4 text-right font-bold text-gray-800" dir="rtl">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-brand-100 rounded-full flex items-center justify-center">
                                  <span className="text-brand-700 font-bold text-sm">
                                    {evaluator.evaluatorName?.charAt(0) || '?'}
                                  </span>
                                </div>
                                <span>{evaluator.evaluatorName || 'Unknown'}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-center font-bold text-brand-600">{evaluator.total_complaints}</td>
                            <td className="px-6 py-4 text-center">
                              <span className="inline-flex items-center px-3 py-1 rounded-xl text-xs font-bold bg-red-100 text-red-800 border border-red-200">
                                {evaluator.rework_analysis.total_rework_count}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className={`inline-flex items-center px-3 py-1 rounded-xl text-xs font-bold border ${
                                evaluator.rework_analysis.rework_rate > 10
                                  ? 'bg-red-100 text-red-800 border-red-200'
                                  : evaluator.rework_analysis.rework_rate > 5
                                  ? 'bg-orange-100 text-orange-800 border-orange-200'
                                  : 'bg-green-100 text-green-800 border-green-200'
                              }`}>
                                {evaluator.rework_analysis.rework_rate.toFixed(1)}%
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center font-medium text-gray-700">
                              {evaluator.rework_analysis.avg_rework_hours}{t('evaluatorReports.allEvaluatorsReport.hours')} {evaluator.rework_analysis.avg_rework_minutes}{t('evaluatorReports.allEvaluatorsReport.minutes')}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </TabPanel>
        </div>
      </div>
    </div>
  );
};

export default EvaluatorReportsPage;
